import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { login } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAccountLinks } from "@/components/useAccountLinks";

const LINKS = [["/#how", "How it works", "how"], ["/#features", "Features", "features"], ["/#gifts", "Gifts", "gifts"], ["/#pricing", "Pricing", "pricing"]];
const item = "block py-3.5 text-lg text-navy border-b border-line hover:text-teal focus-visible:text-teal outline-none transition-colors";

export const MobileMenu = () => {
  const [open, setOpen] = useState(false);
  const acct = useAccountLinks();
  const { logout } = useAuth();
  const btn = useRef(null);
  const panel = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    document.body.style.overflow = "hidden";
    panel.current?.querySelector("a,button")?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") { setOpen(false); btn.current?.focus(); }
      if (e.key === "Tab") {
        const f = panel.current.querySelectorAll("a,button");
        if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); btn.current.focus(); }
        else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); btn.current.focus(); }
      }
    };
    const onResize = () => window.innerWidth >= 768 && setOpen(false);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => { document.body.style.overflow = ""; document.removeEventListener("keydown", onKey); window.removeEventListener("resize", onResize); };
  }, [open]);

  const close = () => setOpen(false);
  return (
    <div className="md:hidden ml-auto">
      <button ref={btn} type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-controls="mobile-menu"
        aria-label={open ? "Close menu" : "Open menu"} data-testid="mobile-menu-toggle"
        className="w-11 h-11 -mr-2 flex items-center justify-center rounded-lg text-navy hover:bg-teal-soft focus-visible:ring-2 focus-visible:ring-teal outline-none transition-colors">
        {open ? <X size={26} /> : <Menu size={26} />}
      </button>
      {open && createPortal(
        <div className="fixed inset-x-0 top-20 bottom-0 z-40 bg-black/20 fade-up" onClick={() => { close(); btn.current?.focus(); }} data-testid="mobile-menu-backdrop">
          <nav id="mobile-menu" ref={panel} aria-label="Main menu" onClick={(e) => e.stopPropagation()}
            className="bg-white border-b border-line shadow-lg px-6 pt-2 pb-6 max-h-full overflow-y-auto" data-testid="mobile-menu">
            {LINKS.map(([href, label, id]) => <Link key={id} to={href} onClick={close} className={item} data-testid={`mobile-nav-${id}`}>{label}</Link>)}
            <Link to="/sample-results" onClick={close} className={item} data-testid="mobile-nav-sample-results">Sample Results</Link>
            {acct && (
              <div className="mt-5" data-testid="mobile-account-section">
                <p className="text-xs uppercase tracking-[0.12em] text-muted-ink">My account · <span className="normal-case tracking-normal">{acct.user.email}</span></p>
                {acct.links.map(([to, label, id]) => <Link key={id} to={to} onClick={close} className={item} data-testid={`mobile-account-${id}`}>{label}</Link>)}
                <button type="button" onClick={() => { close(); logout().then(() => window.location.assign("/")); }} className={`${item} w-full text-left`} data-testid="mobile-account-sign-out">Sign out</button>
              </div>
            )}
            <div className="grid gap-3 mt-6">
              {!acct && <button type="button" onClick={() => { close(); login(); }} className="chip-btn w-full !py-3 !text-base" data-testid="mobile-nav-sign-in">Sign in</button>}
              <Link to="/#pricing" onClick={close} className="btn-teal w-full text-center" data-testid="mobile-nav-get-started">Get started</Link>
            </div>
          </nav>
        </div>,
        document.body,
      )}
    </div>
  );
};
